import React from 'react';
import PropTypes from 'prop-types';
import { autobind } from 'core-decorators';
import { Form, Row, Col, Popconfirm } from 'antd';
import BasePage from 'components/BasePage';
import styledComp, { FlexColContainer, FlexRowContainer } from 'components/StyledComp';
import CustomButton from 'components/CustomButton';
import FormDatePicker from 'components-desktop/FormDatePicker';
import FormInput from 'components-desktop/FormInput';
import FormTextArea from 'components-desktop/FormTextArea';
import Panel from 'components-desktop/Panel';
import helpers from 'misc/helpers';
import TableInvoiceVisits from './components/TableInvoiceVisits';
import TableInvoicePayments from './components/TableInvoicePayments';

const propTypes = {
  form: PropTypes.object.isRequired,
};

@autobind
class InvoiceDetailsPage extends BasePage {
  constructor(props) {
    super(props);

    this.state = {
      invoice: null,
      isSaveButtonEnable: true,
      isPrintButtonEnable: true,

      tableKey: null,
      scheduleVisits: null,
      invoicePayments: null,
    };
  }

  componentDidMount() {
    this.id = this.parseQueryParam('id', this.props.location);
    this.getInvoice();
  }

  getTitle() {
    return 'Invoice Details';
  }

  async getInvoice() {
    const params = { id: this.id };
    const invoice = await this.callServiceForRemoteObject('/invoice/get_model', params);

    this.setState({
      invoice,
    });

    // After getting the invoice, also get its relevant infos.
    const params2 = { invoiceId: this.id };
    const promise1 = this.callServiceForRemoteObjects('/schedule_visit/get_list_by_invoice', params2);
    const promise2 = this.callServiceForRemoteObjects('/invoice_payment/get_list_by_invoice', params2);

    const results = await Promise.all([promise1, promise2]);

    this.setState({
      tableKey: `${Date.now()}_${Math.random()}`,
      scheduleVisits: results[0],
      invoicePayments: results[1],
    });
  }

  async saveInvoice(values) {
    try {
      this.setState({
        isSaveButtonEnable: false,
      });

      const params = {
        id: this.id,
        mailDate: helpers.extraFormDateValue(values, 'mailDate'),
        remark: helpers.extraFormTextAreaValue(values, 'remark'),
      };
      await this.callService('/invoice/update', params);

      this.notifySuccess('Success', 'Invoice updated successfully.');
    } finally {
      this.setState({
        isSaveButtonEnable: true,
      });
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    this.props.form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.saveInvoice(values);
      }
    });
  }

  onResetClick() {
    window.location.reload();
  }

  async onPrintClick() {
    try {
      this.setState({
        isPrintButtonEnable: false,
      });

      const params = { ids: this.id };
      const responseBody = await this.callService('/invoice/export', params);

      // Open a window to show pdf.
      const blob = helpers.base64toBlob(responseBody.getRetBlob(), 'application/pdf');
      window.open(URL.createObjectURL(blob));
    } finally {
      this.setState({
        isPrintButtonEnable: true,
      });
    }
  }

  async onDeleteClick() {
    const { invoice } = this.state;

    if (invoice.getLong('mailDate')) {
      this.notifyWarning('Warning', 'You cannot delete a mailed invoice.');
    } else {
      const params = { id: this.id };
      await this.callService('/invoice/delete', params);

      this.notifySuccess('Success', 'The invoice has been deleted.');

      setTimeout(() => {
        window.location = `/invoices/search_invoices`;
      }, 2000);
    }
  }

  isInRenderableState() {
    return !!this.state.invoice;
  }

  renderContent() {
    const { form } = this.props;
    const {
      invoice,
      isSaveButtonEnable,
      isPrintButtonEnable,
      tableKey,
      scheduleVisits,
      invoicePayments,
    } = this.state;

    return (
      <FlexColContainer
        width="match_parent"
        height="wrap_content"
        alignItems="center"
      >
        <Panel
          width="match_parent"
          height="wrap_content"
          margin="0 0 24px 0"
          color="yellow_gold"
          title={`Invoice Details - ${invoice.getString('invoiceNumber')}`}
          icon="file-text"
          changeHistoryTable="Invoice"
          changeHistoryRowId={Number(this.id)}
        >
          <styledComp.AntdForm
            width="match_parent"
            height="wrap_content"
            onSubmit={this.handleSubmit}
          >
            {/* Content */}
            <FlexColContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
            >
              <Row type="flex" gutter={24}>
                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="invoiceNumber"
                    label="Invoice Number"
                    disabled
                    initialValue={invoice.getString('invoiceNumber')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="authorizationCode"
                    label="Authorization Code"
                    disabled
                    initialValue={invoice.getString('authorizationCode')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="invoiceDateUsString"
                    label="Invoice Date"
                    disabled
                    initialValue={invoice.getString('invoiceDateUsString')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormDatePicker
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="mailDate"
                    label="Mail Date"
                    placeholder="mm/dd/yyyy"
                    extra="Select date from calendar"
                    initialValue={invoice.getMoment('mailDate')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="companyName"
                    label="Company"
                    disabled
                    initialValue={invoice.getString('companyName')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="patientName"
                    label="Patient"
                    disabled
                    initialValue={invoice.getString('patientName')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="serviceStartDateUsString"
                    label="Service Start Date"
                    disabled
                    initialValue={invoice.getString('serviceStartDateUsString')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="serviceEndDateUsString"
                    label="Service End Date"
                    disabled
                    initialValue={invoice.getString('serviceEndDateUsString')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="totalHoursString"
                    label="Total Hours"
                    disabled
                    initialValue={invoice.getString('totalHoursString')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="amount"
                    label="Total Amount"
                    disabled
                    initialValue={helpers.toDollars(invoice.getDouble('amount'))}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="balanceDue"
                    label="Balance Due"
                    disabled
                    initialValue={helpers.toDollars(invoice.getDouble('balanceDue'))}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 8 }} xl={{ span: 6 }}>
                  <FormInput
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="lastExportTimeUsString"
                    label="Last Export Time"
                    disabled
                    initialValue={invoice.getString('lastExportTimeUsString')}
                  />
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                  <FormTextArea
                    width="match_parent"
                    height="wrap_content"
                    form={form}
                    name="remark"
                    label="Remark"
                    rows={3}
                    initialValue={invoice.getString('remark')}
                  />
                </Col>
              </Row>
            </FlexColContainer>

            {/* Footer */}
            <FlexRowContainer
              width="match_parent"
              height="wrap_content"
              padding="20px 20px"
              justifyContent="center"
              backgroundColor="containerGrayBackground"
            >
              <CustomButton
                width="wrap_content"
                height="32px"
                margin="0 4px 0 0"
                backgroundColor="yellow_gold"
                fontSize="14px"
                type="primary"
                loading={!isSaveButtonEnable}
                htmlType="submit"
              >
                SAVE
              </CustomButton>

              <styledComp.AntdButton
                width="wrap_content"
                height="32px"
                margin="0 4px 0 0"
                fontSize="14px"
                type="default"
                onClick={this.onResetClick}
              >
                RESET
              </styledComp.AntdButton>

              <CustomButton
                width="wrap_content"
                height="32px"
                margin="0 64px 0 0"
                backgroundColor="yellow_gold"
                fontSize="14px"
                type="primary"
                loading={!isPrintButtonEnable}
                onClick={this.onPrintClick}
              >
                PRINT INVOICE
              </CustomButton>

              <Popconfirm
                title="Are you sure you want to delete this invoice?"
                placement="top"
                okText="Confirm"
                cancelText="Cancel"
                onConfirm={this.onDeleteClick}
              >
                <styledComp.AntdButton
                  width="wrap_content"
                  height="32px"
                  fontSize="14px"
                  type="danger"
                >
                  DELETE
                </styledComp.AntdButton>
              </Popconfirm>
            </FlexRowContainer>
          </styledComp.AntdForm>
        </Panel>

        {scheduleVisits &&
          <TableInvoiceVisits
            key={`${tableKey}_visits`}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="Invoice Visits"
            scheduleVisits={scheduleVisits}
          />
        }

        {invoicePayments &&
          <TableInvoicePayments
            key={`${tableKey}_payments`}
            width="match_parent"
            height="wrap_content"
            margin="0 0 24px 0"
            title="Invoice Payments"
            invoicePayments={invoicePayments}
          />
        }
      </FlexColContainer>
    );
  }
}

// https://facebook.github.io/react/docs/typechecking-with-proptypes.html
InvoiceDetailsPage.propTypes = propTypes;

export default Form.create()(InvoiceDetailsPage);
